// ──────────────────────────────────────────────────────────────────────────
// sandbox-ng — the test env, a faithful CLONE of cbhi on the SAME modern API
// stack. This file is intentionally cbhi's environment with only the org slug,
// Cognito pool, S3 buckets, and labels changed. The API base URLs are cbhi's
// modern gateways; because every service routes by the {org} path segment,
// setting ORG='sandbox-ng' makes them resolve to /api/sandbox-ng/… → the
// sandbox-ng-* tables (which mirror cbhi-* exactly).
//
// Differs from cbhi ONLY by: ORG, Cognito pool (sandbox pool, sso:false),
// S3 buckets (hc-sandbox-ng-*), and display labels. Keep it in lockstep with
// environment.cbhi.ts when cbhi's API bases change.
//
// The timesheets/timecards/users gateways are the consolidated /prod stage
// deployments, multi-tenant by {org} path, so sandbox-ng uses the same base URLs.
// ──────────────────────────────────────────────────────────────────────────

const URL_CONTRACTS_ALL_ORGS_BASE = 'https://23dwrrrqge.execute-api.us-east-1.amazonaws.com/prod/api/';
const URL_CONTRACTS_BASE = 'https://c5i04533dh.execute-api.us-east-1.amazonaws.com/sandbox/sandbox-ng/';
const URL_BAA_BASE = 'https://bdan7u0te5.execute-api.us-east-1.amazonaws.com/prod/api/';
const URL_COI_BASE = 'https://tk2l6j3hqh.execute-api.us-east-1.amazonaws.com/prod/api/';
const URL_PARTY_BASE = 'https://xjmearl3yd.execute-api.us-east-1.amazonaws.com/prod/api/';
const URL_LEGAL_ENTITY = 'https://359q7pm4b0.execute-api.us-east-1.amazonaws.com/prod/api/';
const URL_USERS_BASE = 'https://12hb1pr57b.execute-api.us-east-1.amazonaws.com/prod/api/';
const URL_DATES_BASE = 'https://sd5uh76ar1.execute-api.us-east-1.amazonaws.com/prod/api/';
const URL_SITES_BASE = 'https://13ugp4yj14.execute-api.us-east-1.amazonaws.com/prod/api/';
const URL_ATTACHMENTS_BASE = 'https://8lq43ya492.execute-api.us-east-1.amazonaws.com/prod/api/';
// Assessments v2 (sign-in, roles, revisions, /summary + /item routes) is live on the backend's
// DEV stage only. sandbox-ng tests against it; switch to prod (sd8x1ci08j/prod) once the backend
// promotes. Dev and prod share the {org}-Assessments tables, so only sandbox-ng may point here.
const URL_ASSESSMENTS_BASE = 'https://5wlbvyvqs7.execute-api.us-east-1.amazonaws.com/dev/api/';
const URL_PROVISIONS_BASE = 'https://6bn4ebzgcc.execute-api.us-east-1.amazonaws.com/prod/api/';
const URL_RELATIONSHIPS_BASE = 'https://of5aj7jyni.execute-api.us-east-1.amazonaws.com/sandbox/api/';
const URL_PEOPLE_BASE = 'https://je202cq2wf.execute-api.us-east-1.amazonaws.com/prod/api/';
const URL_WORKFLOW_BASE = 'https://930mlnyhch.execute-api.us-east-1.amazonaws.com/prod/api/';
const URL_REPORTS_BASE = 'https://dkt4vf7kqj.execute-api.us-east-1.amazonaws.com/prod/api/';
const URL_EMAIL_BASE = 'https://68puniv4m4.execute-api.us-east-1.amazonaws.com/prod/api/';
const URL_REFERENCE_DATA_BASE = 'https://d558uhn5ef.execute-api.us-east-1.amazonaws.com/prod/api/';

// Modern multi-tenant time APIs — both now on the consolidated /prod stage,
// org flows through the {org} path segment. Full routes read .../prod/api/sandbox-ng/….
// (Repointed off the retired zbfueua8cf/cbhi timesheets stage 2026-08-06.)
const URL_TIMESHEETS_BASE = 'https://rk40c7mbe8.execute-api.us-east-1.amazonaws.com/prod/api/';
const URL_TIMECARDS_BASE = 'https://gg04feiab9.execute-api.us-east-1.amazonaws.com/prod/api/';

const URL_DOC_ANALYSIS_BASE = 'https://4tvml6a8se.execute-api.us-east-1.amazonaws.com/prod/api/';

const URL_SCHEDULED_OPS_BASE = 'https://bp7yzpvx4a.execute-api.us-east-1.amazonaws.com/dev/api/';

const ORG = 'sandbox-ng';

export const environment = {
    production: true,

    region: 'us-east-1',

    // Cognito: sandbox-ng uses the SANDBOX pool (separate from cbhi's prod pool).
    // Username/password login (sso:false). The 6 seeded users live here.
    identityPoolId: 'us-east-1:6ed3cea0-6883-4e63-ac30-9a552000e881',
    userPoolId: 'us-east-1_JwlKc2sKe',
    clientId: '6sleo68qv7imskjiqj3g6376e8',

    rekognitionBucket: 'rekognition-pics',
    albumName: "usercontent",
    bucketRegion: 'us-east-1',

    cognito_idp_endpoint: '',
    cognito_identity_endpoint: '',
    sts_endpoint: '',
    dynamodb_endpoint: '',
    s3_endpoint: '',


    // *** BEGINNING OF SUPPORTED CUSTOMIZATIONS ***

    sso: false,

    // User directory + activation state live in cbhi-Users (DynamoDB), not the
    // ssousersallowed Google Sheet. This keeps the admin user list backend-sourced
    // even when sso is flipped back to true. ('sheet' would force the legacy
    // Google-Sheet path; omitting the key falls back to the sso-based default.)
    usersDirectorySource: 'backend',

    // SSO block is inert while sso:false (username/password against the sandbox pool).
    // Pool references point at the sandbox pool for coherence if SSO is ever enabled.
    ssoIssuer: 'https://cognito-idp.us-east-1.amazonaws.com/us-east-1_JwlKc2sKe',
    ssoDomain: 'https://us-east-1mkv1m1tef.auth.us-east-1.amazoncognito.com',
    ssoRedirectUri: 'https://sandbox.healthycontracts.com',
    ssoClientId: '6sleo68qv7imskjiqj3g6376e8',
    responseType: 'token',
    scope: 'openid email',

    // §6 — the cbhi timesheets + timecards backends authorize the actor by the
    // `email` claim, which only the ID token carries (the crestwood access token
    // has no email claim). This flag tells the auth interceptor to send the ID
    // token (not the access token) to URL_TIMESHEETS_BASE / URL_TIMECARDS_BASE.
    // Absent/false on every other org → unchanged access-token behavior there.
    useIdTokenForTimeApis: true,

    /* Entity Lists Available */
    documentReviewList: false,
    assessments: true,

    /* Concierge / command palette (Cmd-K natural-language navigator). Phase 1 =
       frontend-only with a local keyword matcher; wired to an LLM endpoint via
       URL_CONCIERGE_RESOLVE when that ships. */
    conciergeNavigator: true,

    // Use the new or old data tables sheet
    datatables: false,
    dataTableTracks: ['A','C'],

    /* Contract Form */
    // ON in sandbox-ng so workflow can be exercised end to end OUTSIDE production.
    // sandbox-ng-WorkflowRequests exists, so the API returns real results here.
    // cbhi stays off until that test passes.
    workflowManagement: true,
    provisions: true,
    /* Recurring Important Dates: the "Repeats" control on an Important Date.
       ON here so Ray can exercise it end to end OUTSIDE production. The control is
       read as "off unless explicitly true", so a missing key is always safe.
       cbhi was turned ON 2026-10-01 (David) — see the note in environment.cbhi.ts
       for the one thing that still gates it: the roll-forward sweep in
       hc-all-orgs-scheduled-ops being deployed and running. The sweep itself is
       data-driven (it only touches records carrying `recurrence`), so it is harmless
       to run for an org whose UI cannot create one. */
    recurringImportantDates: true,
    providerTimesheets: false,

    /* Timesheets */
    timesheets: true,

    /* NetSuite billing is cbhi-ONLY. Even though this env is the cbhi-shape clone,
       sandbox-ng does NOT bill NetSuite — cbhi's NetSuite is Crestwood's PRODUCTION
       instance, so nothing here should imply a billing path into it. Leave false. */
    netsuiteBilling: false,

    /* Timecards */
    timecards: true,

    /* Accordian Groups */
    salaryAndBenefits: false,
    value: false,
    physicianCompensation: false,
    physicianTimesheet: false,
    realEstateLeaseLandlord: false,
    realEstateLeaseTenant: false,
    equipmentLeasePurchase: false,
    equipmentMaintenanceService: false,
    locumTenens: false,
    hrsaColumns: false,
    hrAccordion: false,
    apAccordion: false,
    acuteCareFinancial: false,
    sochsAccordion: false,
    grants: false,
    leaseManagement: false,

    // sandbox-ng web hosting (hc-sandbox-ng-web + CloudFront E1JI6ZFHRO4VVX).
    linkToDeployment: 'https://sandbox.healthycontracts.com/securehome/',

    // Reuses the shared field-values sheet (generic catalogs: states, activities,
    // PBJ job codes) — same as cbhi; fine for a test env.
    // "LIVE - Sandbox Static Fields" — sandbox-ng's own sheet. Was the LIVE Crestwood sheet
    // (cloned from cbhi) until 2026-11-02. Never point a dev env at a customer's sheet.
    fieldValuesGoogleSheetId: '1E1tsMyD0qNxZN00a0IXG2bm2C85zYlIs5Aw_HVpf6U8',

    org: ORG,
    orgFormalName: 'Sandbox NG Environment',
    orgLocalAdministrator: 'David Berge <a href= \"mailto:david.berge@businessdataapps.com\">david.berge@businessdataapps.com</a>',

    // ASSESSMENTS
    // URL_ASSESSMENTS switches the app to the v2 API (routes hang off it: /summary, /item/{n},
    // /by-entity/{id}). Orgs without it keep using the legacy keys below.
    URL_ASSESSMENTS: URL_ASSESSMENTS_BASE + ORG + '/assessments',
    assessmentsGetPageUrl: URL_ASSESSMENTS_BASE + ORG + '/assessments/page',
    assessmentsGetAllUrl: URL_ASSESSMENTS_BASE + ORG + '/assessments/all',
    assessmentsQueryUrl: URL_ASSESSMENTS_BASE + ORG + '/assessments/query',
    assessmentsQueryListUrl: URL_ASSESSMENTS_BASE + ORG + '/assessments/query-list',
    assessmentsWriteFullUrl: URL_ASSESSMENTS_BASE + ORG + '/assessments/write-full',

    // ATTACHMENTS
    URL_ATTACHMENTS_GET_PRE_SIGNED: URL_ATTACHMENTS_BASE + ORG + '/attachments/pre-signed-url',
    URL_ATTACHMENTS_GET_BY_ID: URL_ATTACHMENTS_BASE + ORG + '/attachments/',
    URL_ATTACHMENTS_PAGE_OF_ATTACHMENTS: URL_ATTACHMENTS_BASE + ORG + '/attachments',
    URL_ATTACHMENTS_SAVE_ATTACHMENT: URL_ATTACHMENTS_BASE + ORG + '/attachments/write',
    URL_ATTACHMENTS_GET_BY_NUMBERS: URL_ATTACHMENTS_BASE + ORG + '/attachments/batch',
    URL_ATTACHMENTS_GET_BY_ENTITY: URL_ATTACHMENTS_BASE + ORG + '/attachments/by-entity/',

    // BAAS
    baaGetAllUrl: URL_BAA_BASE + ORG + '/baas',
    baaWriteUrl: URL_BAA_BASE + ORG + '/baas/write',
    URL_BAA_UPDATE_ATTRIBUTE: URL_BAA_BASE + ORG + '/update-baa-attribute',
    baaGetRecordUrl: URL_BAA_BASE + ORG + 'baas/id/',
    baaGetByNumberUrl: URL_BAA_BASE + ORG + '/baas/',

    // CONTRACTS
    URL_CONTRACTS_PAGE_OF_CONTRACTS: URL_CONTRACTS_ALL_ORGS_BASE + ORG + '/contracts',
    contractWriteUrl: URL_CONTRACTS_ALL_ORGS_BASE + ORG + '/contracts/write',
    contractUpdateAttributeUrl: URL_CONTRACTS_ALL_ORGS_BASE + ORG + '/contracts/update-attribute',
    contractGetByIdUrl: URL_CONTRACTS_ALL_ORGS_BASE + ORG + '/contracts/',
    contractGetByNumberUrl: URL_CONTRACTS_ALL_ORGS_BASE + ORG + '/contracts/by-number/',
    contractsPartiesUrl: URL_CONTRACTS_BASE + 'contract-parties',
    contractsTableDataUrl: URL_CONTRACTS_BASE + 'tabledata',
    contractsAccordionColsUrl: URL_CONTRACTS_BASE + 'contracts-accordion',
    contractsUserRequestedColsUrl: URL_CONTRACTS_BASE + 'contracts-user-cols',
    contractsInactiveUrl: URL_CONTRACTS_BASE + 'inactive',
    contractsGetAssociated: URL_CONTRACTS_BASE + 'associated-contracts',
    contractsSiteFindMatching: URL_CONTRACTS_BASE + 'sites-matching-contracts',
    contractsLimitByColumns: URL_CONTRACTS_BASE + 'contracts-active-limit-cols',
    contractActionsUrl: URL_CONTRACTS_BASE + 'contract-actions/',

    // CONTRACT EXTENSIONS
    URL_CONTRACT_EXTENSION_WRITE: URL_CONTRACTS_BASE + 'write-extension',
    URL_CONTRACT_EXTENSIONS_GET_ALL_OF_TYPE: URL_CONTRACTS_BASE + 'extensions-by-type/',
    URL_CONTRACT_EXTENSIONS_CONTRACT: URL_CONTRACTS_BASE + 'contract-extensions/',

    // COIS
    coiGetAllActiveUrl: URL_COI_BASE + ORG + '/cois',
    coiWriteUrl: URL_COI_BASE + ORG + '/cois/write',
    URL_COI_UPDATE_ATTRIBUTE: URL_COI_BASE + ORG + '/update-coi-attribute',
    coiGetRecordUrl: URL_COI_BASE + ORG + '/cois/id/',
    coiGetUsingNumberUrl: URL_COI_BASE + ORG + '/cois/',

    // CRITICAL DATES
    URL_DATES_GET_PAGE_OF_ALL_DATES: URL_DATES_BASE + ORG + '/page-of-dates',
    URL_DATES_GET_BY_NUMBERS: URL_DATES_BASE + ORG + '/critical-dates/batch',
    criticalDatesAddDateUrl: URL_DATES_BASE + ORG + '/write',
    /* Calendar notification (.ics) for an Important Date, sent on save.
       Backend: hc-all-orgs-scheduled-ops, unauthenticated (authorizationType NONE,
       no authorizers on this gateway) — the interceptor's ID token arrives and is
       ignored. One gateway for every org; the org is a PATH SEGMENT, not a per-env
       host. The service appends the criticalDateId.
       Absent key = feature off, so an org can be left out safely. */
    URL_CRITICAL_DATE_CALENDAR: URL_SCHEDULED_OPS_BASE + 'scheduled-ops/critical-date-calendar/' + ORG + '/',
  // SANDBOX ONLY — reminder test tool (send a test reminder now / "pretend today is").
  // Never copy these two keys into another org's environment file: the backend
  // routes behind them are hardwired to sandbox-ng and reject other logins anyway.
  URL_SANDBOX_REMINDER_TEST: URL_SCHEDULED_OPS_BASE + 'scheduled-ops/sandbox-test/',
  reminderTestTools: true,

    criticalDatesByEntityIdUrl: URL_DATES_BASE + ORG + '/critical-dates/by-entity/',
    criticalDatesByCriticalDateNumUrl: URL_DATES_BASE + ORG + '/critical-dates/',

    // EMAIL
    URL_EMAIL: URL_EMAIL_BASE + ORG + '/email/send',

    // LEGAL ENTITIES
    legalEntityWriteUrl: URL_LEGAL_ENTITY + ORG + '/le/write',
    legalEntityByNumUrl: URL_LEGAL_ENTITY + ORG + '/le/',
    legalEntitiesPaginationUrl: URL_LEGAL_ENTITY + ORG + '/le',
    URL_LEGAL_ENTITIES_GET_PAGE: URL_LEGAL_ENTITY + ORG + '/le',

    // VENDOR | PARTY
    partyWriteUrl: URL_PARTY_BASE + ORG + '/vendors/write',
    URL_PARTIES_PARTIAL_UPDATE: URL_PARTY_BASE + ORG + '/vendors/update-attribute/',
    partyGetByNumUrl: URL_PARTY_BASE + ORG + '/vendors/',
    URL_PARTIES_GET_PAGE: URL_PARTY_BASE + ORG + '/vendors',
    partyAssociateUrl: URL_PARTY_BASE + ORG + '/vendors/associations/',
    partyGetNmcUrl: URL_PARTY_BASE + ORG + '/vendors/nmc/',
    partyWriteDataTableUrl: URL_PARTY_BASE + ORG + '/vendors/datatable',
    partyGetAllPartiesUrl: URL_PARTY_BASE,

    // PEOPLE
    personAssociateUrl: URL_PEOPLE_BASE + ORG + '/associate-person/',
    personGetByNumUrl: URL_PEOPLE_BASE + ORG + '/people/',
    personWriteUrl: URL_PEOPLE_BASE + ORG + '/people/write',
    URL_PEOPLE_GET_PAGE: URL_PEOPLE_BASE + ORG + '/people',
    URL_PEOPLE_UPDATE_ATTRIBUTE: URL_PEOPLE_BASE + ORG + '/people/update-attribute',

    // PROCESSING
    URL_PROCESSING: 'https://5oqiurzjfl.execute-api.us-east-1.amazonaws.com/prod/send-email-with-attachment',

    // PROVISIONS
    provisionContractNumUrl: URL_PROVISIONS_BASE + ORG + '/provisions/contract-num/',
    provisionContractIdUrl: URL_PROVISIONS_BASE + ORG + '/provisions/contract-id/',
    provisionWriteUrl: URL_PROVISIONS_BASE + ORG + '/provisions/write',
    provisionsByPaginationUrl: URL_PROVISIONS_BASE + ORG + '/provisions',

    // RELATIONSHIPS
    URL_RELATIONSHIPS_ADD: URL_RELATIONSHIPS_BASE + ORG + '/relationships/add',
    URL_RELATIONSHIPS_REMOVE: URL_RELATIONSHIPS_BASE + ORG + '/relationships/remove',

    // REPORTS
    URL_REPORTS_WRITE: URL_REPORTS_BASE + ORG + '/reports/write',
    URL_REPORTS_LIST: URL_REPORTS_BASE + ORG + '/reports',
    URL_REPORTS_GET_REPORT: URL_REPORTS_BASE + ORG + '/reports/',
    URL_REPORTS_DELETE_REPORT: URL_REPORTS_BASE + ORG + '/reports/',
    URL_REPORTS_SHARE_REPORT: URL_REPORTS_BASE + ORG + '/reports/',

    URL_REFERENCE_DATA: URL_REFERENCE_DATA_BASE + ORG + '/reference-data/',
    URL_REPORTS_AI_GENERATE: 'https://dkt4vf7kqj.execute-api.us-east-1.amazonaws.com/prod/api/' + ORG + '/reports/ai-generate',

    runReportContractsUrl: 'https://0x1wckgws8.execute-api.us-east-1.amazonaws.com/prod/reporting/contracts/',
    scheduledReportUrl: 'https://xugt0rub76.execute-api.us-east-1.amazonaws.com/',

    // SITES
    siteWriteUrl: URL_SITES_BASE + ORG + '/site/write',
    siteByNumUrl: URL_SITES_BASE + ORG + '/site/',
    sitesPaginationUrl: URL_SITES_BASE + ORG + '/sites',


    // TIMESHEETS (cbhi deployment — hc-all-orgs-timesheets-cbhi, Appendix B)
    URL_TIMESHEETS_BASE: URL_TIMESHEETS_BASE,  // Exposed for the auth interceptor (ID-token, see useIdTokenForTimeApis)
    // Specs
    URL_TIMESHEET_SPECS_WRITE: URL_TIMESHEETS_BASE + ORG + '/timesheet-specs/write',
    URL_TIMESHEET_SPECS_LIST: URL_TIMESHEETS_BASE + ORG + '/timesheet-specs',
    URL_TIMESHEET_SPECS_GET: URL_TIMESHEETS_BASE + ORG + '/timesheet-specs/',
    URL_TIMESHEET_SPECS_BY_CONTRACT: URL_TIMESHEETS_BASE + ORG + '/timesheet-specs/by-contract/',
    URL_TIMESHEET_SPECS_BY_USER: URL_TIMESHEETS_BASE + ORG + '/timesheet-specs/by-user/',
    URL_TIMESHEET_SPECS_REPLACE: URL_TIMESHEETS_BASE + ORG + '/timesheet-specs/',
    URL_TIMESHEET_SPECS_REPLACED: URL_TIMESHEETS_BASE + ORG + '/timesheet-specs/replaced',
    URL_TIMESHEET_SPECS_FINANCIALS: URL_TIMESHEETS_BASE + ORG + '/timesheet-specs/',
    // Submissions — submit/recall (owner) stay on update-period (§7); back-half moves to /approvals/*
    URL_TIMESHEET_SUBMISSIONS_LOG_HOURS: URL_TIMESHEETS_BASE + ORG + '/submissions/log-hours/',
    URL_TIMESHEET_SUBMISSIONS_WRITE_PERIOD: URL_TIMESHEETS_BASE + ORG + '/submissions/write-period/',
    URL_TIMESHEET_SUBMISSIONS_UPDATE_PERIOD: URL_TIMESHEETS_BASE + ORG + '/submissions/update-period/',
    URL_TIMESHEET_SUBMISSIONS_BY_CONTRACT: URL_TIMESHEETS_BASE + ORG + '/submissions/by-contract/',
    URL_TIMESHEET_SUBMISSIONS_PERIOD: URL_TIMESHEETS_BASE + ORG + '/submissions/period/',
    URL_TIMESHEET_SUBMISSIONS_DAY: URL_TIMESHEETS_BASE + ORG + '/submissions/day/',
    URL_TIMESHEET_SUBMISSIONS_LIST: URL_TIMESHEETS_BASE + ORG + '/submissions',
    URL_TIMESHEET_SUBMISSIONS_ASSIGNED: URL_TIMESHEETS_BASE + ORG + '/submissions/assigned/',  // service appends userEmail; server-bucketed forReview/forApproval/forPayment
    URL_TIMESHEET_ADMIN_SUBMISSIONS_ACTIVE: URL_TIMESHEETS_BASE + ORG + '/submissions/admin/active',  // System-admin ONLY: ALL in-flight submissions (every HCP/period), same bucketed shape as ASSIGNED
    // Back-half approval actions (§7 / Appendix B) — discrete routes, service appends {submissionId}.
    // Actor derived from ID token (no changedBy in body). reject/bounce require { note }.
    URL_TIMESHEET_APPROVALS_REVIEW:          URL_TIMESHEETS_BASE + ORG + '/approvals/review/',
    URL_TIMESHEET_APPROVALS_APPROVE:         URL_TIMESHEETS_BASE + ORG + '/approvals/approve/',
    URL_TIMESHEET_APPROVALS_REJECT:          URL_TIMESHEETS_BASE + ORG + '/approvals/reject/',
    URL_TIMESHEET_APPROVALS_BOUNCE:          URL_TIMESHEETS_BASE + ORG + '/approvals/bounce/',
    URL_TIMESHEET_APPROVALS_PENDING_PAYMENT: URL_TIMESHEETS_BASE + ORG + '/approvals/pending-payment/',
    URL_TIMESHEET_APPROVALS_PAID:            URL_TIMESHEETS_BASE + ORG + '/approvals/paid/',


    // TIMECARDS (cbhi deployment — Appendix B)
    URL_TIMECARDS_BASE: URL_TIMECARDS_BASE,  // Exposed for the auth interceptor (ID-token, see useIdTokenForTimeApis)
    URL_TIMECARD_SPECS_WRITE:    URL_TIMECARDS_BASE + ORG + '/timecard-specs/write',
    URL_TIMECARD_SPECS_GET:      URL_TIMECARDS_BASE + ORG + '/timecard-specs/',
    URL_TIMECARD_SPECS_BY_PARTY: URL_TIMECARDS_BASE + ORG + '/timecard-specs/by-party/',
    URL_TIMECARD_SPECS_LIST:     URL_TIMECARDS_BASE + ORG + '/timecard-specs',
    URL_TIMECARD_SPECS_REPLACE:  URL_TIMECARDS_BASE + ORG + '/timecard-specs/replace',

    // Timecard approvals (My Work) — action-param style { principalEmail, periodKey, action:'approve'|'return', note? }.
    // periodKey replaces the legacy yearMonth; send_back_to_reviewer is gone (§3/§10). Confirm note-vs-reason key with a live call.
    URL_TIMECARD_ADMIN_SUBMISSIONS: URL_TIMECARDS_BASE + ORG + '/admin/submissions',
    URL_TIMECARD_ADMIN_SUBMISSIONS_ACTIVE: URL_TIMECARDS_BASE + ORG + '/admin/submissions/active',  // System-admin: ALL in-flight submissions (all HCPs/months), bucketed
    // Every submission for ONE HCP, all periods — a base-table Query on principalEmail,
    // paged to completion. Replaces one ?month= call per period in the Timecard Ledger.
    URL_TIMECARD_ADMIN_SUBMISSIONS_HCP: URL_TIMECARDS_BASE + ORG + '/admin/submissions/hcp/',
    URL_TIMECARD_ADMIN_HCP:         URL_TIMECARDS_BASE + ORG + '/admin/timecards/hcp/',
    URL_TIMECARD_APPROVALS_REVIEW:  URL_TIMECARDS_BASE + ORG + '/approvals/review',
    URL_TIMECARD_APPROVALS_APPROVE: URL_TIMECARDS_BASE + ORG + '/approvals/approve',
    URL_TIMECARD_SUBMISSIONS_ASSIGNED: URL_TIMECARDS_BASE + ORG + '/submissions/assigned/',  // GET; service appends {callerEmail}; self-only; userEmail + forReview/forApproval (2 tabs, no payment)


    // HC-USERS (proxy-assignments + user management) — dedicated cbhi deployment
    // (gateway 76lthajid3, stage cbhi). People pickers are UNAUTHENTICATED, so they
    // work immediately and return people once cbhi-Users is populated. When proxy
    // admin is wired (its endpoints switch to ID-token), the interceptor's
    // useIdTokenForTimeApis flag must extend to THIS base too — see memory
    // cbhi-proxy-admin-idtoken-flag. Until then the base stays on the access-token side.
    URL_USERS_BASE: URL_USERS_BASE,  // Exposed for the auth interceptor to detect hc-users API calls
    URL_PROXY_ASSIGNMENT_GRANT:           URL_USERS_BASE + ORG + '/proxy-assignments',
    URL_PROXY_ASSIGNMENT_REVOKE:          URL_USERS_BASE + ORG + '/proxy-assignments/',
    URL_PROXY_ASSIGNMENTS_BY_PROXY:       URL_USERS_BASE + ORG + '/proxy-assignments/by-proxy',
    URL_PROXY_ASSIGNMENTS_BY_PRINCIPAL:   URL_USERS_BASE + ORG + '/proxy-assignments/by-principal',


    // @deprecated — old property names kept for compilation only.
    timesheetsSpecFinancialsUrl: '',
    timesheetsReplacedSpecListUrl: '',
    timesheetsWriteSpecUrl: '',
    timesheetsSpecListUrl: '',
    timesheetsGetSpecUrl: '',
    timesheetsGetWorkflowStatusUrl: '',
    timesheetsGetSubmissionPeriodUrl: '',
    timesheetsUpdateSubmissionPeriodUrl: '',
    timesheetsReturnAllSubmissionsUrl: '',


    // ALL ORGS USERS
    URL_USER_WRITE_METADATA: URL_USERS_BASE + ORG + '/users/metadata',
    URL_USERS_NT_PAGE_OF_USERS: URL_USERS_BASE + ORG + '/users',
    URL_USERS_NT_GET_USER_BY_EMAIL: URL_USERS_BASE + ORG + '/user-email',
    URL_USERS_NT_GET_USER_BY_ID: URL_USERS_BASE + ORG + '/user',
    URL_USERS_NT_UPDATE_PERMISSIONS: URL_USERS_BASE + ORG + '/users/update-user-permissions',
    URL_USERS_NT_SET_ACTIVATION: URL_USERS_BASE + ORG + '/users/set-activation',
    // Per-user automatic-email opt-outs (Manage Users → Emails).
    URL_USERS_NT_SET_EMAIL_OPT_OUTS: URL_USERS_BASE + ORG + '/users/set-email-opt-outs',
    URL_USERS_NT_CREATE_USER: URL_USERS_BASE + ORG + '/users/create',
    URL_USERS_NT_DELETE_USER: URL_USERS_BASE + ORG + '/users/delete',
    URL_USERS_NT_VIEW_SAVE_LIST_VIEW: URL_USERS_BASE + ORG + '/users/save-list-view',
    URL_USERS_NT_VIEW_REMOVE: URL_USERS_BASE + ORG + '/users/remove-view',
    URL_USERS_NT_VIEW_USER_VIEW: URL_USERS_BASE + ORG + '/user-view/',
    URL_USERS_NT_USER_PROFILE_WRITE: URL_USERS_BASE + ORG + '/users/profile',

    // USERS
    URL_USERS_GET_PAGE_OF_USERS: URL_USERS_BASE + 'page-of-users',
    URL_USER_PERMISSIONS: '',

    // WORKFLOW
    URL_WORKFLOW_CREATE: URL_WORKFLOW_BASE + ORG + '/workflow-requests',
    URL_WORKFLOW_LIST: URL_WORKFLOW_BASE + ORG + '/workflow-requests',
    URL_WORKFLOW_GET: URL_WORKFLOW_BASE + ORG + '/workflow-requests/',
    URL_WORKFLOW_UPDATE: URL_WORKFLOW_BASE + ORG + '/workflow-requests/',

    workflowRouting: {
      contract:  'admin@example.com',
      baa:       'admin@example.com',
      coi:       'admin@example.com',
      addendum:  'admin@example.com',
      unknown:   'admin@example.com'
    } as Record<string, string>,

    // @deprecated — legacy document-review workflow endpoints (return 502). Do not use.
    workflowUrlGetDocumentsForEntity: 'https://tvwl5awaba.execute-api.us-east-1.amazonaws.com/prod/documents/',
    workflowUrlNewRequest: 'https://tvwl5awaba.execute-api.us-east-1.amazonaws.com/prod/new-workflow-request/',
    workflowUrlUpdateAttribute: 'https://tvwl5awaba.execute-api.us-east-1.amazonaws.com/prod/update/',
    workflowUrlGetRequests: 'https://tvwl5awaba.execute-api.us-east-1.amazonaws.com/prod/requests/',
    workflowUrlGetRequest: 'https://tvwl5awaba.execute-api.us-east-1.amazonaws.com/prod/request/',
    workflowUrlGenerateDoc: 'https://tvwl5awaba.execute-api.us-east-1.amazonaws.com/prod/create-document',


    domainForDocumentReviews: 'https://68vdgl90y4.execute-api.us-east-1.amazonaws.com/prod/',
    documentReviewsKeyForProd: 'LXKpua06q77YfFHddZH6I6oJS68hZMMN326TrgPW',

    domainForCommunications: 'https://ykkbbhr0k9.execute-api.us-east-1.amazonaws.com/prod/',
    communicationsKeyForProd: 'FUpjSpAF0r5A0SBoCIKHQ6F8xbDsr3Dp1UsT4o40',


    // DOCUMENT ANALYSIS
    URL_DOCUMENT_ANALYSIS_TRIGGER: URL_DOC_ANALYSIS_BASE + ORG + '/contracts/{contractId}/ai-analysis/trigger',
    URL_DOCUMENT_STATUS: URL_DOC_ANALYSIS_BASE + ORG + '/contracts/{contractId}/ai-analysis',
    // Provision gap analysis ("Analysis" tab) — same hc-document-analysis gateway (4tvml6a8se).
    URL_PROVISION_ANALYSIS_TRIGGER: URL_DOC_ANALYSIS_BASE + ORG + '/contracts/{contractId}/provision-analysis/trigger',
    URL_PROVISION_ANALYSIS_STATUS: URL_DOC_ANALYSIS_BASE + ORG + '/contracts/{contractId}/provision-analysis',
    URL_DOCUMENT_INTAKE_TRIGGER: URL_DOC_ANALYSIS_BASE + ORG + '/contracts/intake/ai-analysis',
    URL_DOCUMENT_INTAKE_STATUS: URL_DOC_ANALYSIS_BASE + ORG + '/contracts/intake/ai-analysis/{analysisId}',
    URL_INTAKE_CHAT: URL_DOC_ANALYSIS_BASE + ORG + '/intake/chat',


    domainForCrudOps: "https://tdvw41fdol.execute-api.us-east-1.amazonaws.com/",

    domainForAccess: "https://zxz2zvo64a.execute-api.us-east-1.amazonaws.com/",

    s3BucketLink: "hc-sandbox-ng-attachments", // NO slash on end!!!
    s3Location: "https://hc-sandbox-ng-attachments.s3.amazonaws.com/",

    // DEV Environments
    devDomainForCrudOps: "https://tdvw41fdol.execute-api.us-east-1.amazonaws.com/prod/",


    // *** END OF SUPPORTED CUSTOMIZATIONS ***

};
